/**
 * Service worker regisztráció és frissítés-kezelés.
 *
 * - Csak production buildben regisztrál (fejlesztésben a korábban telepített
 *   workert eltávolítja, hogy ne térítse el a localhost:3000-et).
 * - `REACT_APP_DISABLE_SW=true` buildváltozóval vészfékként ki is kapcsolható:
 *   ilyenkor a meglévő regisztrációt is törli.
 * - Ha új verzió vár aktiválásra (waiting worker), `onUpdate`-et hív – akkor is,
 *   ha a waiting worker már a regisztráció pillanatában ott van (pl. a felhasználó
 *   korábban bezárta a fület, és csak most jött vissza).
 * - `applyUpdate` SKIP_WAITING üzenetet küld, és a controllerchange után EGYSZER
 *   újratölt – csak abban a fülben, amelyik a frissítést kérte.
 */

import { SKIP_WAITING_MESSAGE } from './serviceWorkerRoutes';

export interface ServiceWorkerConfig {
  /** Új verzió települt és vár aktiválásra. */
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  /** Első telepítés kész (a tartalom offline is elérhető). */
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
}

/** Ennyi időnként keresünk új verziót egy nyitva hagyott alkalmazásban. */
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

const isSupported = (): boolean =>
  typeof window !== 'undefined' && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

export const shouldRegister = (): boolean =>
  process.env.NODE_ENV === 'production' && process.env.REACT_APP_DISABLE_SW !== 'true';

const whenLoaded = (): Promise<void> =>
  new Promise((resolve) => {
    if (document.readyState === 'complete') {
      resolve();
    } else {
      window.addEventListener('load', () => resolve(), { once: true });
    }
  });

const trackInstalling = (
  registration: ServiceWorkerRegistration,
  worker: ServiceWorker | null,
  config: ServiceWorkerConfig
): void => {
  if (!worker) return;
  worker.addEventListener('statechange', () => {
    if (worker.state !== 'installed') return;
    if (navigator.serviceWorker.controller) {
      // Volt már aktív verzió → ez frissítés, a régi még kiszolgál
      config.onUpdate?.(registration);
    } else {
      config.onSuccess?.(registration);
    }
  });
};

let stopUpdateChecks: (() => void) | null = null;

/** Időszakos és fülre-visszatéréskori frissítéskeresés (egyszerre csak egy példány). */
const startUpdateChecks = (registration: ServiceWorkerRegistration): void => {
  stopUpdateChecks?.();
  const checkForUpdate = () => {
    registration.update().catch(() => {
      // offline vagy szerverhiba – a következő alkalommal újra próbáljuk
    });
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  };
  const intervalId = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  stopUpdateChecks = () => {
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    stopUpdateChecks = null;
  };
};

/**
 * Regisztrálja a service workert. A visszaadott Promise a regisztrációval
 * (vagy null-lal, ha nem regisztrált) teljesül.
 */
export async function register(config: ServiceWorkerConfig = {}): Promise<ServiceWorkerRegistration | null> {
  if (!isSupported()) return null;

  if (!shouldRegister()) {
    await unregister();
    return null;
  }

  // A PUBLIC_URL más originre mutatva a service worker nem működne
  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
  if (publicUrl.origin !== window.location.origin) return null;

  await whenLoaded();

  try {
    const registration = await navigator.serviceWorker.register(`${process.env.PUBLIC_URL || ''}/service-worker.js`);

    // Már várakozó új verzió (korábbi látogatásból) → azonnal jelezzük
    if (registration.waiting && navigator.serviceWorker.controller) {
      config.onUpdate?.(registration);
    }

    // Éppen települő verzió
    trackInstalling(registration, registration.installing, config);
    registration.addEventListener('updatefound', () => {
      trackInstalling(registration, registration.installing, config);
    });

    // Nyitva hagyott alkalmazásnál időnként és visszatéréskor is keresünk frissítést
    startUpdateChecks(registration);

    return registration;
  } catch (error) {
    console.error('Service worker regisztráció sikertelen:', error);
    return null;
  }
}

let reloadScheduled = false;

/**
 * Az új (waiting) verzió aktiválása: SKIP_WAITING üzenet, majd egyetlen
 * újratöltés, amikor az új worker átvette az irányítást.
 */
export function applyUpdate(
  registration: ServiceWorkerRegistration,
  reload: () => void = () => window.location.reload()
): void {
  const waiting = registration.waiting;
  if (!waiting || !isSupported()) {
    // Nincs mit aktiválni (pl. egy másik fül már megtette) → elég egy újratöltés
    if (!reloadScheduled) {
      reloadScheduled = true;
      reload();
    }
    return;
  }

  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloadScheduled) return;
    reloadScheduled = true;
    reload();
  });
  waiting.postMessage(SKIP_WAITING_MESSAGE);
}

/** Csak teszteléshez: az egyszeri újratöltés-jelző visszaállítása. */
export function __resetForTests(): void {
  reloadScheduled = false;
  stopUpdateChecks?.();
}

export async function unregister(): Promise<void> {
  if (!isSupported()) return;
  try {
    if (typeof navigator.serviceWorker.getRegistrations !== 'function') return;
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch {
    // nincs regisztráció / nem támogatott – nincs teendő
  }
}
